/**
 * useViewportHeight - Dynamic viewport height hook for mobile browsers
 * 
 * Handles the dynamic browser bar (address bar / bottom bar) on mobile browsers.
 * Uses the Visual Viewport API for accurate measurements and CSS custom properties
 * for seamless integration with CSS.
 * 
 * Features:
 * - Real visible viewport height (not 100vh which is static)
 * - Handles browser bar appearing/disappearing during scroll
 * - Handles device orientation changes
 * - Handles mobile keyboard appearance
 * - Updates CSS custom property (--vh) for use in stylesheets
 * - Smooth visual behavior without jumps
 * 
 * Usage:
 * - Import and call useViewportHeight() in your root component
 * - Use calc(var(--vh, 1vh) * 100) or var(--viewport-height) in CSS
 */

import { useEffect, useCallback, useRef } from 'react';

// Throttle function to limit how often updates happen
const throttle = (func, limit) => {
  let inThrottle;
  return function(...args) {
    if (!inThrottle) {
      func.apply(this, args);
      inThrottle = true;
      setTimeout(() => inThrottle = false, limit);
    }
  };
};

/**
 * Custom hook to manage viewport height dynamically
 * @param {Object} options - Configuration options
 * @param {number} options.throttleMs - Throttle interval for resize/scroll events (default: 100ms)
 * @param {boolean} options.updateOnScroll - Whether to update on scroll events (default: true)
 * @returns {Object} - Current viewport dimensions and update function
 */
const useViewportHeight = (options = {}) => {
  const {
    throttleMs = 100,
    updateOnScroll = true,
  } = options;

  const previousHeight = useRef(0);
  const rafId = useRef(null);

  /**
   * Calculate and set the viewport height CSS custom properties
   */
  const updateViewportHeight = useCallback(() => {
    // Cancel any pending animation frame
    if (rafId.current) {
      cancelAnimationFrame(rafId.current);
    }

    rafId.current = requestAnimationFrame(() => {
      let vh;
      let viewportHeight;

      // Use Visual Viewport API if available (more accurate for mobile)
      if (window.visualViewport) {
        viewportHeight = window.visualViewport.height;
        vh = viewportHeight * 0.01;
      } else {
        // Fallback to window.innerHeight
        viewportHeight = window.innerHeight;
        vh = viewportHeight * 0.01;
      }

      // Only update if height actually changed (avoid unnecessary repaints)
      if (Math.abs(previousHeight.current - viewportHeight) > 1) {
        previousHeight.current = viewportHeight;
        
        const root = document.documentElement;
        
        // Set --vh unit (1% of viewport height)
        root.style.setProperty('--vh', `${vh}px`);
        
        // Set full viewport height for convenience
        root.style.setProperty('--viewport-height', `${viewportHeight}px`);
        
        // Set viewport width (useful for some layouts)
        const vw = (window.visualViewport?.width || window.innerWidth) * 0.01;
        root.style.setProperty('--vw', `${vw}px`);
        root.style.setProperty('--viewport-width', `${window.visualViewport?.width || window.innerWidth}px`);
        
        // Set offset values if Visual Viewport is available
        if (window.visualViewport) {
          root.style.setProperty('--viewport-offset-top', `${window.visualViewport.offsetTop}px`);
          root.style.setProperty('--viewport-offset-left', `${window.visualViewport.offsetLeft}px`);
          root.style.setProperty('--viewport-scale', `${window.visualViewport.scale}`);
        }
      }
    });
  }, []);

  // Create throttled version for scroll events
  const throttledUpdate = useCallback(
    throttle(updateViewportHeight, throttleMs),
    [updateViewportHeight, throttleMs]
  );

  useEffect(() => {
    // Initial calculation
    updateViewportHeight();

    // Use Visual Viewport API events if available (preferred)
    if (window.visualViewport) {
      // Resize event fires when viewport size changes (including browser bar)
      window.visualViewport.addEventListener('resize', updateViewportHeight);
      
      // Scroll event fires when viewport position changes (keyboard, etc.)
      if (updateOnScroll) {
        window.visualViewport.addEventListener('scroll', throttledUpdate);
      }
    }

    // Window resize as fallback and for orientation changes
    window.addEventListener('resize', updateViewportHeight);
    
    // Orientation change handler (important for mobile)
    window.addEventListener('orientationchange', () => {
      // Small delay to let the browser finish orientation change
      setTimeout(updateViewportHeight, 100);
      setTimeout(updateViewportHeight, 300); // Double-check after animation
    });

    // Also update on page scroll (handles some edge cases)
    if (updateOnScroll) {
      window.addEventListener('scroll', throttledUpdate, { passive: true });
    }

    // Cleanup
    return () => {
      if (rafId.current) {
        cancelAnimationFrame(rafId.current);
      }
      
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', updateViewportHeight);
        if (updateOnScroll) {
          window.visualViewport.removeEventListener('scroll', throttledUpdate);
        }
      }
      
      window.removeEventListener('resize', updateViewportHeight);
      window.removeEventListener('orientationchange', updateViewportHeight);
      
      if (updateOnScroll) {
        window.removeEventListener('scroll', throttledUpdate);
      }
    };
  }, [updateViewportHeight, throttledUpdate, updateOnScroll]);

  return {
    updateViewportHeight,
  };
};

export default useViewportHeight;

/**
 * Standalone initialization function for non-React usage
 * Call this once at app startup
 */
export const initViewportHeight = (options = {}) => {
  const { throttleMs = 100, updateOnScroll = true } = options;
  let previousHeight = 0;
  let rafId = null;

  const throttle = (func, limit) => {
    let inThrottle;
    return function(...args) {
      if (!inThrottle) {
        func.apply(this, args);
        inThrottle = true;
        setTimeout(() => inThrottle = false, limit);
      }
    };
  };

  const updateViewportHeight = () => {
    if (rafId) cancelAnimationFrame(rafId);

    rafId = requestAnimationFrame(() => {
      let vh, viewportHeight;

      if (window.visualViewport) {
        viewportHeight = window.visualViewport.height;
        vh = viewportHeight * 0.01;
      } else {
        viewportHeight = window.innerHeight;
        vh = viewportHeight * 0.01;
      }

      if (Math.abs(previousHeight - viewportHeight) > 1) {
        previousHeight = viewportHeight;
        
        const root = document.documentElement;
        root.style.setProperty('--vh', `${vh}px`);
        root.style.setProperty('--viewport-height', `${viewportHeight}px`);
        
        const vw = (window.visualViewport?.width || window.innerWidth) * 0.01;
        root.style.setProperty('--vw', `${vw}px`);
        root.style.setProperty('--viewport-width', `${window.visualViewport?.width || window.innerWidth}px`);
        
        if (window.visualViewport) {
          root.style.setProperty('--viewport-offset-top', `${window.visualViewport.offsetTop}px`);
          root.style.setProperty('--viewport-offset-left', `${window.visualViewport.offsetLeft}px`);
          root.style.setProperty('--viewport-scale', `${window.visualViewport.scale}`);
        }
      }
    });
  };

  const throttledUpdate = throttle(updateViewportHeight, throttleMs);

  updateViewportHeight();

  if (window.visualViewport) {
    window.visualViewport.addEventListener('resize', updateViewportHeight);
    if (updateOnScroll) {
      window.visualViewport.addEventListener('scroll', throttledUpdate);
    }
  }

  window.addEventListener('resize', updateViewportHeight);
  window.addEventListener('orientationchange', () => {
    setTimeout(updateViewportHeight, 100);
    setTimeout(updateViewportHeight, 300);
  });

  if (updateOnScroll) {
    window.addEventListener('scroll', throttledUpdate, { passive: true });
  }

  return updateViewportHeight;
};

